import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Empty, Typography, Button } from 'antd';
import { InfoCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import RCTable from 'components/custom-table/RCTable';
import StatusHistoryModal from 'pages/trip/components/StatusHistoryModal';
import { getUIVersion } from 'utils/router-static-data';

const { Text } = Typography;

function RequestHistoryTab({ tableDatas, togglePaginate, dispatchAction }) {
	const navigate = useNavigate();
	const isV2 = React.useMemo(() => getUIVersion() === 'new', []);
	const [statusHistoryVisible, setStatusHistoryVisible] = useState(false);
	const [selectedRequestId, setSelectedRequestId] = useState(null);

	const handleViewStatusHistory = (record) => {
		setSelectedRequestId(record.id);
		setStatusHistoryVisible(true);
	};

	const handleCloseStatusHistory = () => {
		setStatusHistoryVisible(false);
		setSelectedRequestId(null);
	};

	const handleView = (record) => {
		navigate(`/finance/requests/${record.id}?sourceTab=request-history`);
	};

	const columns = [
		{
			title: 'Trip ID',
			dataIndex: 'trip_id',
			key: 'trip_id',
			width: 90,
			render: (trip_id) => <Text overFlow="hidden">{trip_id || '-'}</Text>,
		},
		{
			title: 'Request ID',
			dataIndex: 'adhoc_request_id',
			key: 'adhoc_request_id',
			width: 100,
		},
		{
			title: 'Request Type',
			dataIndex: 'request_type',
			key: 'request_type',
			width: 120,
		},
		{
			title: 'Business Unit',
			dataIndex: 'business_unit_name',
			key: 'business_unit_name',
			width: 130,
		},
		{
			title: 'Party Name',
			dataIndex: 'party_name',
			key: 'party_name',
			width: 150,
		},
		{
			title: 'Request Date',
			dataIndex: 'request_date',
			key: 'request_date',
			width: 150,
			render: (date) => {
				if (!date) return '-';
				return moment(date).add(5, 'hours').add(30, 'minutes').format('DD/MM/YYYY h:mm A');
			},
		},
		{
			title: 'Request Value',
			dataIndex: 'request_value',
			key: 'request_value',
			width: 130,
			render: (value) => (value ? `₹${parseFloat(value).toLocaleString('en-IN')}` : '-'),
		},
		{
			title: 'Finance Request Status',
			dataIndex: 'status',
			key: 'status',
			width: 100,
			render: (text, record) => (
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<span>{text || '-'}</span>
					<Button
						type="text"
						icon={<InfoCircleOutlined />}
						size="small"
						onClick={() => handleViewStatusHistory(record)}
						style={{ padding: 0, height: 'auto' }}
					/>
				</div>
			),
		},
		{
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
			width: 100,
			render: (_, record) => (
				<Button type="link" icon={<EyeOutlined />} onClick={() => handleView(record)} />
			),
		},
	];

	return (
		<>
			<div style={{ marginTop: 16 }}>
				<RCTable
					rowKey="id"
					tableDatas={tableDatas}
					togglePaginate={togglePaginate}
					dispatchAction={dispatchAction}
					columns={columns}
					scroll={{ x: true }}
					locale={{
						emptyText: (
							<Empty
								description={<Text type="secondary">No request history found</Text>}
							/>
						),
					}}
				/>
			</div>
			<StatusHistoryModal
				visible={statusHistoryVisible}
				onClose={handleCloseStatusHistory}
				adhocRequestId={selectedRequestId}
				fetchStatusHistoryAction={isV2 ? 'finance_v2' : 'finance'}
			/>
		</>
	);
}

RequestHistoryTab.propTypes = {
	tableDatas: PropTypes.array.isRequired,
	togglePaginate: PropTypes.array.isRequired,
	dispatchAction: PropTypes.func.isRequired,
};

export default RequestHistoryTab;
